import {
  Box,
  CircularProgress,
  Dialog,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import useFetch from '../../../api/useFetch';
import {
  ExoTableToolbar,
  ExoTableToolbarItem,
} from '../../../components/exo/ExoTableToolbar';
import { useTranslation } from 'react-i18next';
import AutoFixNormalIcon from '@mui/icons-material/AutoFixNormal';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestorePageIcon from '@mui/icons-material/RestorePage';
import { GenerateReimbursements } from './GenerateReimbursements';
import { useMemo, useState } from 'react';
import ExoAccordion from '../../../components/exo/ExoAccordion';
import ExoAlert from '../../../components/exo/ExoAlert';
import ExoAvatar from '../../../components/exo/ExoAvatar';
import { ExoFilePreview } from '../../../components/exo/ExoFilePreview';
import Feedback from '../../../components/special/Feedback';
import MoneyLabel from '../../../components/label/MoneyLabel';
import moment from 'moment';
import { API_DELETE, API_POST, DBRequest, useConfig } from '../../../api/api';
import { tokens } from '../../../global/theme/tokens';

const ReimbursementsTab = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();

  const { apiData, setApiData, isLoading, reloadApiData } = useFetch(
    'travel-expense-reimbursements?groupBy=endDate&sortBy=startDate&perPage=12'
  );

  const [open, setOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);

  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: '',
  });
  function handleAlertOpen(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }

  function handleNewElement() {
    setOpen(false);
    reloadApiData();
  }

  function handleDelete(monthKey, deletedRow) {
    setApiData((prev) => ({
      ...prev,
      data: prev.data
        .map((group) => {
          if (group.month !== monthKey) return group;
          return {
            ...group,
            items: group.items.filter((item) => item.id !== deletedRow.id),
          };
        })
        .filter((group) => group.items.length > 0),
    }));
  }

  function handleRegenerate(monthKey, newItem, oldItem) {
    setApiData((prev) => ({
      ...prev,
      data: prev.data.map((group) => {
        if (group.month !== monthKey) return group;
        return {
          ...group,
          items: group.items.map((item) =>
            item.id === oldItem.id ? newItem.data : item
          ),
        };
      }),
    }));
  }

  function getMonthTotal(items) {
    return items.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
  }

  function sortItemsAlphabetically(items) {
    return [...items].sort((a, b) => {
      const nameA = `${a.user?.lastName} ${a.user?.firstName}`.toLowerCase();
      const nameB = `${b.user?.lastName} ${b.user?.firstName}`.toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }

  const accordionElements = useMemo(
    () =>
      apiData?.data?.map((group) => ({
        customSummary: (
          <Box className="flex flex-row justify-between items-center w-full pr-2">
            <Typography sx={{ fontWeight: 600, fontSize: '1rem' }}>
              {moment(group.month, 'YYYY-MM').format('MMMM YYYY')}
            </Typography>
            <Box className="flex flex-row items-center gap-3">
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                <MoneyLabel money={getMonthTotal(group.items)} />
              </Typography>
            </Box>
          </Box>
        ),
        content: (
          <Box className="flex flex-col">
            {sortItemsAlphabetically(group.items).map((item) => (
              <EmployeeRow
                key={item.id}
                item={item}
                colors={colors}
                config={config}
                onPreview={() => setPreviewFile(item.file)}
                onDelete={() => handleDelete(group.month, item)}
                onRegenerate={(newItem) =>
                  handleRegenerate(group.month, newItem, item)
                }
                t={t}
              />
            ))}
          </Box>
        ),
      })) || [],
    [apiData, colors, config]
  );

  return (
    <Box className="w-full flex flex-col gap-0 h-full">
      <ExoTableToolbar>
        <ExoTableToolbarItem
          text={t('Generate')}
          icon={<AutoFixNormalIcon />}
          onClick={() => setOpen(true)}
        />
      </ExoTableToolbar>
      {isLoading ? (
        <Box className="flex justify-center items-center py-8">
          <Typography sx={{ opacity: 0.5 }}>{t('Loading')}...</Typography>
        </Box>
      ) : accordionElements.length === 0 ? (
        <Box className="flex justify-center items-center py-8">
          <Typography sx={{ opacity: 0.5 }}>
            {t('No data available')}
          </Typography>
        </Box>
      ) : (
        <ExoAccordion elements={accordionElements} />
      )}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <GenerateReimbursements
          onResponse={handleNewElement}
          onClose={() => setOpen(false)}
          onFeedback={handleAlertOpen}
        />
      </Dialog>
      <Feedback state={alertState} setState={setAlertState} />
      <ExoFilePreview
        open={Boolean(previewFile)}
        src={previewFile?.temporaryUrl}
        mimeType={previewFile?.mimeType || 'application/pdf'}
        onClose={() => setPreviewFile(null)}
      />
    </Box>
  );
};

const EmployeeRow = ({
  item,
  colors,
  config,
  onPreview,
  onDelete,
  onRegenerate,
  t,
}) => {
  const user = item.user;

  // Regenerating renders the PDF server-side and takes a moment; the button
  // turns into a spinner meanwhile so the click has visible feedback and
  // cannot be repeated mid-flight.
  const [regenerating, setRegenerating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  function handleDelete() {
    setConfirmDelete(false);
    DBRequest({
      config,
      path: `travel-expense-reimbursements/${item.id}`,
      method: API_DELETE(),
      onResponse: () => onDelete(),
    });
  }

  function handleRegenerate() {
    if (regenerating) return;

    DBRequest({
      config,
      path: `travel-expense-reimbursements/${item.id}/regenerate`,
      method: API_POST({}),
      onLoading: setRegenerating,
      onResponse: (res) => onRegenerate(res),
    });
  }

  return (
    <Box
      className="flex flex-row items-center justify-between px-3 py-2 rounded-lg transition-colors"
      sx={{
        '&:hover': {
          backgroundColor: colors.bgInput,
        },
      }}
    >
      <Box className="flex flex-row items-center gap-3">
        <ExoAvatar
          picture={user?.picture?.temporaryUrl || ''}
          name={`${user?.firstName || ''} ${user?.lastName || ''}`}
          size="36px"
        />
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {user?.lastName} {user?.firstName}
        </Typography>
      </Box>
      <Box className="flex flex-row items-center gap-2">
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          {item.tripCount ?? 0} {t('Trips')}
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.7 }}>
          <MoneyLabel money={item.totalPrice || 0} />
        </Typography>
        <IconButton size="small" onClick={onPreview} title={t('Preview')}>
          <VisibilityIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          onClick={handleRegenerate}
          disabled={regenerating}
          title={t('Regenerate')}
        >
          {regenerating ? (
            <CircularProgress size={16} />
          ) : (
            <RestorePageIcon fontSize="small" />
          )}
        </IconButton>
        <IconButton
          size="small"
          onClick={() => setConfirmDelete(true)}
          title={t('Delete')}
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>
      <ExoAlert
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onClickAway={() => setConfirmDelete(false)}
        onSubmit={handleDelete}
        alert={{
          type: 'warning',
          message: t('Are you sure you want to delete this entry?'),
          submitLabel: t('Delete'),
          cancelLabel: t('Cancel'),
        }}
      />
    </Box>
  );
};

export default ReimbursementsTab;
